import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Button } from "@/components/ui/button";
import { Camera, Image, Check, RotateCcw } from "lucide-react";
import Header from "@/components/Header";
import { useToast } from "@/components/ui/use-toast";
import { useNativeFeatures } from "@/hooks/use-native";
import { usePhotoState } from "@/hooks/use-photo-state";
import { addLog } from "@/logger";

const AddPhoto = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [streamActive, setStreamActive] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const { isNative } = useNativeFeatures();
  const { savePhoto, photoCount } = usePhotoState();

  // Handle browser camera stream
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setStreamActive(true);
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      addLog(`Error accessing camera ${error} `);
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStreamActive(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context?.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return;

          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result as string;
            setCapturedImage(base64data); // ✅ Set here after async read
          };
          reader.readAsDataURL(blob);
        },
        "image/jpeg",
        0.85
      );

      stopCamera(); // ✅ Still safe to stop the stream right after
    }
  };
  const handleNativePhoto = async (source: CameraSource) => {
    try {
      const image = await CapacitorCamera.getPhoto({
        source,
        resultType: CameraResultType.DataUrl,
        quality: 90,
      });
      const dataUrl = image.dataUrl ?? (image.base64String ? `data:image/jpeg;base64,${image.base64String}` : null);
      if (dataUrl) setCapturedImage(dataUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!msg.includes("cancel") && !msg.includes("Cancel")) {
        addLog(`Capacitor Camera error: ${msg}`);
        toast({ title: "Photo Error", description: "Could not get photo.", variant: "destructive" });
      }
    }
  };

  const handleCameraClick = () => {
    if (isNative) {
      void handleNativePhoto(CameraSource.Camera);
    } else {
      startCamera();
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    if (isNative) {
      handleCameraClick();
    } else {
      startCamera();
    }
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    if (isNative) {
      void handleNativePhoto(CameraSource.Photos);
    } else if (fileInputRef.current) {
      fileInputRef.current.accept = "image/*";
      if ("capture" in fileInputRef.current) {
        delete fileInputRef.current.capture;
      }
      fileInputRef.current.click();
    }
  };

  const savePhotoAndReturn = async () => {
    if (!capturedImage) return;

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `grievance-photo-${timestamp}.jpg`;

    try {
      await savePhoto(capturedImage, filename);

      toast({
        title: "Photo Saved",
        description: `Your photo has been successfully saved. Total photos: ${
          photoCount + 1
        }`,
      });

      navigate("/add-case");
    } catch (error) {
      console.error("Failed to save photo:", error);

      toast({
        title: "Save Failed",
        description: "There was an issue saving your photo. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Cleanup camera when component unmounts
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="flex flex-col min-h-[100dvh] bg-background">
      <Header
        title={`Add Supporting Photo ${
          photoCount > 0 ? `(${photoCount} added)` : ""
        }`}
        showBackButton
      />

      <main className="flex-1 pt-16 pb-6 px-4 flex flex-col safe-area-bottom">
        {photoCount > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              You have {photoCount} photo{photoCount !== 1 ? "s" : ""} saved for
              this grievance.
            </p>
          </div>
        )}

        <div className="relative mb-6 flex-1 flex flex-col justify-center">
          {!capturedImage ? (
            <>
              <div className="relative bg-black rounded-xl overflow-hidden mb-4 aspect-[4/3] flex items-center justify-center">
                {streamActive ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center p-4 text-white">
                    <Camera size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Camera preview will appear here</p>
                  </div>
                )}
              </div>

              {/* Hidden canvas for capturing */}
              <canvas ref={canvasRef} className="hidden" />

              <div className="flex justify-center gap-4">
                {!streamActive ? (
                  <Button onClick={handleCameraClick} className="flex-1 gap-2">
                    <Camera size={18} />
                    Take Photo
                  </Button>
                ) : (
                  <Button onClick={capturePhoto} className="flex-1 gap-2">
                    <Camera size={18} />
                    Capture
                  </Button>
                )}

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelection}
                  accept="image/*"
                  className="hidden"
                />

                <Button
                  variant="outline"
                  onClick={handleUploadClick}
                  className="flex-1 gap-2"
                >
                  <Image size={18} />
                  Upload Photo
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-black rounded-xl overflow-hidden mb-4 aspect-[4/3]">
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={retakePhoto}
                  className="flex-1 gap-2"
                >
                  <RotateCcw size={18} />
                  Retake
                </Button>

                <Button onClick={savePhotoAndReturn} className="flex-1 gap-2">
                  <Check size={18} />
                  Save & Return
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AddPhoto;
